import React, { useCallback, useState } from 'react';
import { Calendar, Save, RefreshCw, Download, Clock, Settings, BookOpen, Users, CheckSquare, Building } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import BasicSettingsSection from './BasicSettingsSection';
import TimeSettingsSection from './TimeSettingsSection';
import SubjectsSection from './SubjectsSection';
import RoomsSection from './RoomsSection';
import GroupsSection from './GroupsSection';
import TeachersSection from './TeachersSection';
import OptimizationSettingsSection from './OptimizationSettingsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';
import { HOLIDAYS_RUSSIA_2024 } from '../../../../../utils/constants/holidays';

/**
 * Компонент основной формы генератора расписаний
 */
const GeneratorForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isGenerating, 
  setIsGenerating,
  generatedSchedule,
  setGeneratedSchedule,
  scheduleTypes,
  saveTemplate 
}) => {
  
  // Состояние для имени сохраняемого шаблона
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Текущая активная секция настроек
  const [activeSection, setActiveSection] = useState('basic'); // 'basic', 'time', 'subjects', 'rooms', 'groups', 'teachers', 'optimization'
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev };
      
      // Обрабатываем вложенные поля
      if (name.includes('.')) {
        const [section, field] = name.split('.');
        newState[section] = {
          ...prev[section],
          [field]: fieldValue
        };
      } else {
        newState[name] = fieldValue;
      }

      // Особая обработка рабочих дней
      if (name === 'workDays' && Array.isArray(fieldValue)) {
        newState.workDays = [...fieldValue].sort();
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Обработчик изменения массивов в форме (предметы, кабинеты, группы, учителя)
  const handleArrayChange = useCallback((arrayName, action, item, index) => {
    setFormData(prev => {
      const newState = { ...prev };
      const currentArray = [...prev[arrayName]];

      switch (action) {
        case 'add':
          if (item) {
            const newId = Date.now().toString();
            newState[arrayName] = [...currentArray, { ...item, id: newId }];
          }
          break;
        case 'update':
          if (item && index !== undefined) {
            currentArray[index] = { ...currentArray[index], ...item };
            newState[arrayName] = [...currentArray];
          } else if (item && item.id) {
            const itemIndex = currentArray.findIndex(i => i.id === item.id);
            if (itemIndex >= 0) {
              currentArray[itemIndex] = { ...currentArray[itemIndex], ...item };
              newState[arrayName] = [...currentArray];
            }
          }
          break;
        case 'remove':
          if (index !== undefined) {
            newState[arrayName] = currentArray.filter((_, i) => i !== index);
          } else if (item && item.id) {
            newState[arrayName] = currentArray.filter(i => i.id !== item.id);
          }
          break;
        default:
          break;
      }

      return newState;
    });
  }, [setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    
    // Валидация основных параметров
    if (!formData.scheduleType) {
      newErrors.scheduleType = 'Выберите тип расписания';
    }
    
    if (!formData.startDate) {
      newErrors.startDate = 'Укажите дату начала';
    }
    
    if (!formData.endDate) {
      newErrors.endDate = 'Укажите дату окончания';
    }
    
    if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) {
      newErrors.endDate = 'Дата окончания должна быть позже даты начала';
    }
    
    if (!formData.workDays || formData.workDays.length === 0) {
      newErrors.workDays = 'Выберите хотя бы один рабочий день';
    }
    
    // Валидация временных параметров
    if (!formData.dayStartTime) {
      newErrors.dayStartTime = 'Укажите время начала дня';
    }
    
    if (!formData.dayEndTime) {
      newErrors.dayEndTime = 'Укажите время окончания дня';
    }
    
    if (formData.dayStartTime && formData.dayEndTime) {
      const start = formData.dayStartTime.split(':').map(Number);
      const end = formData.dayEndTime.split(':').map(Number);
      
      const startMinutes = start[0] * 60 + start[1];
      const endMinutes = end[0] * 60 + end[1];
      
      if (startMinutes >= endMinutes) {
        newErrors.dayEndTime = 'Время окончания должно быть позже времени начала';
      }
    }
    
    if (formData.lunchBreak) {
      if (!formData.lunchStartTime) {
        newErrors.lunchStartTime = 'Укажите время начала обеда';
      }
      
      if (!isPositiveNumber(formData.lunchDuration)) {
        newErrors.lunchDuration = 'Укажите длительность обеда';
      } else if (parseInt(formData.lunchDuration) < 15) {
        newErrors.lunchDuration = 'Минимальная длительность обеда — 15 минут';
      } else if (parseInt(formData.lunchDuration) > 120) {
        newErrors.lunchDuration = 'Максимальная длительность обеда — 120 минут';
      }
    }
    
    if (!isPositiveNumber(formData.eventDuration)) {
      newErrors.eventDuration = 'Укажите длительность занятия';
    } else if (parseInt(formData.eventDuration) < 15) {
      newErrors.eventDuration = 'Минимальная длительность занятия — 15 минут';
    } else if (parseInt(formData.eventDuration) > 180) {
      newErrors.eventDuration = 'Максимальная длительность занятия — 180 минут';
    }
    
    if (!isPositiveNumber(formData.breakDuration)) {
      newErrors.breakDuration = 'Укажите длительность перерыва';
    } else if (parseInt(formData.breakDuration) < 5) {
      newErrors.breakDuration = 'Минимальная длительность перерыва — 5 минут';
    } else if (parseInt(formData.breakDuration) > 30) {
      newErrors.breakDuration = 'Максимальная длительность перерыва — 30 минут';
    }
    
    // Валидация предметов, кабинетов, групп и учителей
    if (!formData.subjects || formData.subjects.length === 0) {
      newErrors.subjects = 'Добавьте хотя бы один предмет';
    }
    
    if (!formData.groups || formData.groups.length === 0) {
      newErrors.groups = 'Добавьте хотя бы одну группу/класс';
    }
    
    if (!formData.teachers || formData.teachers.length === 0) {
      newErrors.teachers = 'Добавьте хотя бы одного преподавателя';
    }
    
    // Проверка связей между предметами и преподавателями
    const assignedSubjects = formData.teachers.flatMap(teacher => teacher.subjects);
    const unassignedSubjects = formData.subjects.filter(subject => !assignedSubjects.includes(subject.id));
    
    if (unassignedSubjects.length > 0) {
      newErrors.teachersSubjects = `Следующие предметы не назначены преподавателям: ${unassignedSubjects.map(s => s.name).join(', ')}`;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция генерации расписания
  const generateSchedule = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsGenerating(true);
    setGeneratedSchedule(null);

    // Используем setTimeout для обновления UI перед началом генерации
    setTimeout(() => {
      try {
        // --- Подготовка данных для генерации ---
        const startDate = new Date(formData.startDate);
        const endDate = new Date(formData.endDate);
        const workDays = formData.workDays.map(day => parseInt(day));
        
        // Формирование списка дат
        const allDates = [];
        const currentDate = new Date(startDate);
        
        while (currentDate <= endDate) {
          // Проверяем, является ли текущий день рабочим днем
          const dayOfWeek = currentDate.getDay(); // 0 - воскресенье, 1 - понедельник, ...
          
          if (workDays.includes(dayOfWeek)) {
            // Проверяем, не является ли текущий день праздничным
            const dateString = currentDate.toISOString().split('T')[0];
            const isHoliday = formData.holidaysInclude && HOLIDAYS_RUSSIA_2024.includes(dateString);
            const isCustomHoliday = formData.customHolidays.includes(dateString);
            
            if (!isHoliday && !isCustomHoliday) {
              allDates.push(new Date(currentDate));
            }
          }
          
          // Переходим к следующему дню
          currentDate.setDate(currentDate.getDate() + 1);
        }
        
        // Парсинг времени
        const parseTime = (timeString) => {
          const [hours, minutes] = timeString.split(':').map(Number);
          return hours * 60 + minutes; // Возвращаем время в минутах
        };
        
        const dayStartMinutes = parseTime(formData.dayStartTime);
        const dayEndMinutes = parseTime(formData.dayEndTime);
        const lunchStartMinutes = formData.lunchBreak ? parseTime(formData.lunchStartTime) : null;
        const lunchEndMinutes = formData.lunchBreak ? lunchStartMinutes + parseInt(formData.lunchDuration) : null;
        const eventDurationMinutes = parseInt(formData.eventDuration);
        const breakDurationMinutes = parseInt(formData.breakDuration);
        
        // Генерация временных слотов
        const generateTimeSlots = (date) => {
          const slots = [];
          let currentMinute = dayStartMinutes;
          
          while (currentMinute + eventDurationMinutes <= dayEndMinutes) {
            // Проверяем, не пересекается ли слот с обедом
            const slotEndMinute = currentMinute + eventDurationMinutes;
            const isLunchOverlap = formData.lunchBreak && 
              ((currentMinute < lunchEndMinutes && slotEndMinute > lunchStartMinutes) ||
               (currentMinute >= lunchStartMinutes && currentMinute < lunchEndMinutes));
            
            if (!isLunchOverlap) {
              const startHour = Math.floor(currentMinute / 60);
              const startMin = currentMinute % 60;
              const endHour = Math.floor(slotEndMinute / 60);
              const endMin = slotEndMinute % 60;
              
              const startTime = `${String(startHour).padStart(2, '0')}:${String(startMin).padStart(2, '0')}`;
              const endTime = `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
              
              slots.push({
                date: new Date(date),
                startTime,
                endTime,
                startMinutes: currentMinute,
                endMinutes: slotEndMinute
              });
            }
            
            // Переходим к следующему потенциальному слоту (добавляем длительность занятия и перерыв)
            currentMinute = slotEndMinute + breakDurationMinutes;
            
            // Если следующий слот начинается до обеда, но заканчивается после обеда,
            // перемещаем его начало на конец обеда
            if (formData.lunchBreak && currentMinute < lunchStartMinutes && currentMinute + eventDurationMinutes > lunchStartMinutes) {
              currentMinute = lunchEndMinutes;
            }
          }
          
          return slots;
        };
        
        // Генерация всех возможных временных слотов для всех дат
        const allTimeSlots = allDates.flatMap(generateTimeSlots);
        
        // Алгоритм назначения занятий
        // Для каждой группы распределяем предметы по временным слотам,
        // учитывая нагрузку преподавателей и доступность кабинетов
        const assignments = [];
        
        // Для каждой группы распределяем нагрузку по предметам
        formData.groups.forEach(group => {
          // Подсчет необходимых часов в неделю для каждого предмета
          const subjects = formData.subjects.map(subject => ({
            ...subject,
            hoursRemaining: subject.hoursPerWeek // Копия, чтобы не изменять оригинал
          }));
          
          // Находим слоты для текущей группы
          const availableSlots = [...allTimeSlots]; // Копия, чтобы не изменять оригинал
          
          // Распределяем предметы по слотам
          subjects.forEach(subject => {
            // Находим преподавателей для данного предмета
            const subjectTeachers = formData.teachers.filter(teacher => 
              teacher.subjects.includes(subject.id)
            );
            
            // Если у предмета нет часов или преподавателей, пропускаем
            if (subject.hoursRemaining <= 0 || subjectTeachers.length === 0) {
              return;
            }
            
            // Определяем, сколько занятий нужно назначить для данного предмета
            const lessonsNeeded = Math.ceil(subject.hoursRemaining * 60 / eventDurationMinutes);
            
            for (let i = 0; i < lessonsNeeded; i++) {
              // Находим свободный слот, когда преподаватель доступен
              for (let slotIndex = 0; slotIndex < availableSlots.length; slotIndex++) {
                const slot = availableSlots[slotIndex];
                
                // Проверяем доступность преподавателей в этот слот
                const availableTeacher = subjectTeachers.find(teacher => {
                  // Проверяем, не занят ли учитель в этот слот другими занятиями
                  const isTeacherBusy = assignments.some(assignment => 
                    assignment.teacher === teacher.id &&
                    assignment.date.toDateString() === slot.date.toDateString() &&
                    assignment.startTime === slot.startTime
                  );
                  
                  return !isTeacherBusy;
                });
                
                if (!availableTeacher) continue;
                
                // Находим свободную аудиторию
                const availableRoom = formData.rooms.find(room => {
                  // Проверяем, не занята ли аудитория в этот слот
                  const isRoomBusy = assignments.some(assignment => 
                    assignment.room === room.id &&
                    assignment.date.toDateString() === slot.date.toDateString() &&
                    assignment.startTime === slot.startTime
                  );
                  
                  return !isRoomBusy;
                });
                
                if (!availableRoom) continue;
                
                // Назначаем занятие
                assignments.push({
                  id: Date.now() + Math.random().toString(36).substr(2, 9),
                  subject: subject.id,
                  group: group.id,
                  teacher: availableTeacher.id,
                  room: availableRoom.id,
                  date: new Date(slot.date),
                  startTime: slot.startTime,
                  endTime: slot.endTime,
                  startMinutes: slot.startMinutes,
                  endMinutes: slot.endMinutes
                });
                
                // Удаляем использованный слот
                availableSlots.splice(slotIndex, 1);
                
                // Уменьшаем оставшиеся часы предмета
                subject.hoursRemaining -= eventDurationMinutes / 60;
                
                // Если предмет уже полностью распределен, выходим из цикла
                if (subject.hoursRemaining <= 0) {
                  break;
                }
                
                break;
              }
            }
          });
        });
        
        // Обогащаем назначения информацией о предметах, группах, преподавателях и кабинетах
        const enrichedAssignments = assignments.map(assignment => {
          const subject = formData.subjects.find(s => s.id === assignment.subject);
          const group = formData.groups.find(g => g.id === assignment.group);
          const teacher = formData.teachers.find(t => t.id === assignment.teacher);
          const room = formData.rooms.find(r => r.id === assignment.room);
          
          return {
            ...assignment,
            subjectName: subject ? subject.name : 'Неизвестный предмет',
            subjectColor: subject ? subject.color : '#cccccc',
            groupName: group ? group.name : 'Неизвестная группа',
            teacherName: teacher ? teacher.name : 'Неизвестный преподаватель',
            roomName: room ? room.name : 'Неизвестная аудитория'
          };
        });
        
        // Группировка занятий по дням и по неделям для разных представлений
        const groupedByDay = {};
        const groupedByWeek = {};
        const groupedByGroup = {};
        
        enrichedAssignments.forEach(assignment => {
          const dateStr = assignment.date.toISOString().split('T')[0];
          const weekStart = new Date(assignment.date);
          weekStart.setDate(weekStart.getDate() - weekStart.getDay() + (weekStart.getDay() === 0 ? -6 : 1)); // Получаем понедельник текущей недели
          const weekKey = weekStart.toISOString().split('T')[0];
          
          // Группировка по дням
          if (!groupedByDay[dateStr]) {
            groupedByDay[dateStr] = [];
          }
          groupedByDay[dateStr].push(assignment);
          
          // Группировка по неделям
          if (!groupedByWeek[weekKey]) {
            groupedByWeek[weekKey] = {};
          }
          if (!groupedByWeek[weekKey][dateStr]) {
            groupedByWeek[weekKey][dateStr] = [];
          }
          groupedByWeek[weekKey][dateStr].push(assignment);
          
          // Группировка по группам
          if (!groupedByGroup[assignment.group]) {
            groupedByGroup[assignment.group] = {};
          }
          if (!groupedByGroup[assignment.group][dateStr]) {
            groupedByGroup[assignment.group][dateStr] = [];
          }
          groupedByGroup[assignment.group][dateStr].push(assignment);
        });
        
        // Финальный результат
        const result = {
          schedule: enrichedAssignments,
          byDay: groupedByDay,
          byWeek: groupedByWeek,
          byGroup: groupedByGroup,
          startDate,
          endDate,
          subjects: formData.subjects,
          groups: formData.groups,
          teachers: formData.teachers,
          rooms: formData.rooms,
          meta: {
            totalDays: allDates.length,
            totalAssignments: enrichedAssignments.length,
            generatedAt: new Date()
          }
        };
        
        setGeneratedSchedule(result);
      } catch (error) {
        console.error("Ошибка генерации расписания:", error);
        setErrors({ general: `Произошла ошибка при генерации расписания: ${error.message}. Пожалуйста, проверьте введенные данные и попробуйте снова.` });
        setGeneratedSchedule(null);
      } finally {
        setIsGenerating(false);
      }
    }, 100);
  }, [formData, validateForm, errors, setErrors, setGeneratedSchedule, setIsGenerating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      scheduleType: 'weekly',
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(new Date().setMonth(new Date().getMonth() + 3)).toISOString().split('T')[0],
      workDays: ['1', '2', '3', '4', '5'],
      dayStartTime: '09:00',
      dayEndTime: '18:00',
      lunchBreak: true,
      lunchStartTime: '13:00',
      lunchDuration: 60,
      holidaysInclude: true,
      customHolidays: [],
      eventDuration: 45,
      breakDuration: 10,
      subjects: [
        { id: '1', name: 'Математика', color: '#4F46E5', hoursPerWeek: 4, teacher: 'Иванов И.И.' },
        { id: '2', name: 'Русский язык', color: '#16A34A', hoursPerWeek: 3, teacher: 'Петрова П.П.' },
        { id: '3', name: 'Информатика', color: '#CA8A04', hoursPerWeek: 2, teacher: 'Сидоров С.С.' }
      ],
      rooms: [
        { id: '1', name: '101', capacity: 30, equipment: ['проектор', 'компьютеры'] },
        { id: '2', name: '102', capacity: 25, equipment: ['проектор'] },
        { id: '3', name: '103', capacity: 20, equipment: ['лабораторное оборудование'] }
      ],
      groups: [
        { id: '1', name: '11А', size: 25 },
        { id: '2', name: '11Б', size: 23 }
      ],
      teachers: [
        { id: '1', name: 'Иванов И.И.', subjects: ['1'], availability: [] },
        { id: '2', name: 'Петрова П.П.', subjects: ['2'], availability: [] },
        { id: '3', name: 'Сидоров С.С.', subjects: ['3'], availability: [] }
      ],
      optimizationRules: {
        distributeEvenly: true,
        avoidLateStart: true,
        prioritizeRoomPreferences: true,
        balanceLoad: true
      }
    });
    setGeneratedSchedule(null);
    setErrors({});
    setSaveTemplateName('');
    setShowSaveDialog(false);
  };

  // Экспорт расписания в CSV
  const exportToCsv = () => {
    if (!generatedSchedule || !generatedSchedule.schedule || generatedSchedule.schedule.length === 0) {
      alert("Нет данных для экспорта. Сначала сгенерируйте расписание.");
      return;
    }

    // Определяем заголовки
    const headers = ['Дата', 'День недели', 'Начало', 'Окончание', 'Предмет', 'Группа', 'Преподаватель', 'Аудитория'];

    // Дни недели на русском
    const daysOfWeek = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];

    // Преобразуем данные в строки CSV
    const rows = generatedSchedule.schedule.map(item => {
      const date = new Date(item.date);
      const formattedDate = date.toLocaleDateString('ru-RU');
      const dayOfWeek = daysOfWeek[date.getDay()];

      return [
        formattedDate,
        dayOfWeek,
        item.startTime,
        item.endTime,
        item.subjectName,
        item.groupName,
        item.teacherName,
        item.roomName
      ].join(';');
    });

    // Добавляем общую информацию в начало файла
    const infoRows = [
      ['Расписание', ''],
      ['Период:', `${new Date(generatedSchedule.startDate).toLocaleDateString('ru-RU')} - ${new Date(generatedSchedule.endDate).toLocaleDateString('ru-RU')}`],
      ['Всего дней:', generatedSchedule.meta.totalDays],
      ['Всего занятий:', generatedSchedule.meta.totalAssignments],
      ['Дата генерации:', new Date(generatedSchedule.meta.generatedAt).toLocaleString('ru-RU')],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расписание_занятий.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения шаблона
  const handleSaveTemplate = () => {
    if (showSaveDialog) {
      const savedResult = saveTemplate(saveTemplateName || null);
      if (savedResult) {
        alert(`Шаблон "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveTemplateName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    generateSchedule();
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="mb-8">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Calendar className="w-6 h-6 text-indigo-500" />
          Генератор расписания
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Кнопки переключения секций */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 dark:border-gray-700 pb-4">
          <button
            type="button"
            onClick={() => setActiveSection('basic')}
            className={`px-3 py-2 text-sm font-medium rounded-md ${
              activeSection === 'basic'
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
            }`}
          >
            <Calendar className="inline-block w-4 h-4 mr-1" />
            Основные
          </button>
          <button
            type="button"
            onClick={() => setActiveSection('time')}
            className={`px-3 py-2 text-sm font-medium rounded-md ${
              activeSection === 'time'
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
            }`}
          >
            <Clock className="inline-block w-4 h-4 mr-1" />
            Время
          </button>
          <button
            type="button"
            onClick={() => setActiveSection('subjects')}
            className={`px-3 py-2 text-sm font-medium rounded-md ${
              activeSection === 'subjects'
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
            }`}
          >
            <BookOpen className="inline-block w-4 h-4 mr-1" />
            Предметы
          </button>
          <button
            type="button"
            onClick={() => setActiveSection('rooms')}
            className={`px-3 py-2 text-sm font-medium rounded-md ${
              activeSection === 'rooms'
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
            }`}
          >
            <Building className="inline-block w-4 h-4 mr-1" />
            Кабинеты
          </button>
          <button
            type="button"
            onClick={() => setActiveSection('groups')}
            className={`px-3 py-2 text-sm font-medium rounded-md ${
              activeSection === 'groups'
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
            }`}
          >
            <Users className="inline-block w-4 h-4 mr-1" />
            Группы
          </button>
          <button
            type="button"
            onClick={() => setActiveSection('teachers')}
            className={`px-3 py-2 text-sm font-medium rounded-md ${
              activeSection === 'teachers'
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
            }`}
          >
            <Users className="inline-block w-4 h-4 mr-1" />
            Преподаватели
          </button>
          <button
            type="button"
            onClick={() => setActiveSection('optimization')}
            className={`px-3 py-2 text-sm font-medium rounded-md ${
              activeSection === 'optimization'
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'
                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
            }`}
          >
            <Settings className="inline-block w-4 h-4 mr-1" />
            Оптимизация
          </button>
        </div>

        {/* Основные параметры */}
        {activeSection === 'basic' && (
          <BasicSettingsSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
            scheduleTypes={scheduleTypes}
          />
        )}

        {/* Параметры времени */}
        {activeSection === 'time' && (
          <TimeSettingsSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
          />
        )}

        {/* Предметы */}
        {activeSection === 'subjects' && (
          <SubjectsSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            handleArrayChange={handleArrayChange}
            errors={errors} 
          />
        )}

        {/* Кабинеты */}
        {activeSection === 'rooms' && (
          <RoomsSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            handleArrayChange={handleArrayChange}
            errors={errors} 
          />
        )}

        {/* Группы */}
        {activeSection === 'groups' && (
          <GroupsSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            handleArrayChange={handleArrayChange}
            errors={errors} 
          />
        )}

        {/* Преподаватели */}
        {activeSection === 'teachers' && (
          <TeachersSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            handleArrayChange={handleArrayChange}
            errors={errors} 
            subjects={formData.subjects}
          />
        )}

        {/* Параметры оптимизации */}
        {activeSection === 'optimization' && (
          <OptimizationSettingsSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
          />
        )}

        {/* Диалог сохранения шаблона */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveTemplateName}
                onChange={(e) => setSaveTemplateName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveTemplate}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calendar className="w-5 h-5" />}
            disabled={isGenerating || Object.keys(errors).length > 0}
            isLoading={isGenerating}
            size="lg"
          >
            {isGenerating ? 'Генерация...' : 'Создать расписание'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isGenerating}
            size="lg"
          >
            Сбросить
          </Button>
          {generatedSchedule && (
            <Button
              type="button"
              onClick={exportToCsv}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isGenerating}
              size="lg"
            >
              Скачать расписание (CSV)
            </Button>
          )}
          {generatedSchedule && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveTemplate}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isGenerating}
              size="lg"
            >
              Сохранить шаблон
            </Button>
          )}
        </div>

        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default GeneratorForm;